import { Form, Formik } from 'formik';
import { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';

// ASSETS
import { EditIcon } from 'assets';

// COMPONENTS
import { PrimaryButton, SecondaryButton, SVGButton } from 'components/Buttons';
import FlashMessage from 'components/FlashMessage';
import Field from 'components/FormField';
import { useModal } from 'components/Modals';
import { StyledCancelButton } from 'components/Modals/styles';

// CONSTANTS
import { FLASH_MESSAGE_TYPES, flashMessageFactory, INITIAL_FLASH_MESSAGE } from 'constants/common';

// HOOKS
import { useAppDispatch } from 'hooks/store';

// STYLES
import { BUTTON_INTENT } from 'styles/buttons';
import colors from 'styles/colors';
import { ModalContent, Modal as ModalDialog, ModalFooter, ModalHeader, Title } from 'styles/modals';

// THUNKS
import {
  addDealershipDepartment,
  removeDealershipDepartment,
  updateDealershipDepartment,
} from 'services/DealershipSettings/thunks';

// TYPES
import { DealershipDepartment } from 'services/resources';
import { StyledAddDepartmentButton } from '../styles';

const useDepartmentForm = (
  department: DealershipDepartment | undefined,
  {
    isEdit = false,
    setFlashMessage,
  }: { isEdit?: boolean; setFlashMessage: (flashMessage: typeof INITIAL_FLASH_MESSAGE) => void }
) => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .max(
            255,
            formatMessage(
              { id: 'validators.yup.string.max' },
              { path: formatMessage({ id: 'views.FleetSettings.Departments.inputLabel' }), max: 255 }
            )
          )
          .required(
            formatMessage(
              { id: 'validators.yup.mixed.required' },
              { path: formatMessage({ id: 'views.FleetSettings.Departments.inputLabel' }) }
            )
          ),
      }),
    [formatMessage]
  );

  const onSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      if (isEdit) {
        await dispatch(updateDealershipDepartment(department.id, values));
      } else {
        await dispatch(addDealershipDepartment(values));
      }
      const flashMessage = flashMessageFactory({
        shown: true,
        translationId: isEdit
          ? 'views.FleetSettings.Departments.flashMessage.editSuccess'
          : 'views.FleetSettings.Departments.flashMessage.creationSuccess',
      });
      setFlashMessage(flashMessage);
    } catch (error) {
      const flashMessage = flashMessageFactory({
        shown: true,
        type: FLASH_MESSAGE_TYPES.error,
        translationId: 'views.FleetSettings.Departments.flashMessage.creationFailed',
      });
      setFlashMessage(flashMessage);
    }
    setSubmitLoading(false);
  };

  const onDelete = async () => {
    try {
      setDeleteLoading(true);
      await dispatch(removeDealershipDepartment(department.id));
      const flashMessage = flashMessageFactory({
        shown: true,
        type: FLASH_MESSAGE_TYPES.success,
        translationId: 'views.FleetSettings.Departments.flashMessage.deleteSuccess',
      });
      setFlashMessage(flashMessage);
    } catch (err) {
      const flashMessage = flashMessageFactory({
        shown: true,
        type: FLASH_MESSAGE_TYPES.error,
        translationId: 'views.FleetSettings.Departments.flashMessage.deleteFailed',
      });
      setFlashMessage(flashMessage);
    }
  };

  let initialValues = {
    name: '',
  };

  if (department) {
    initialValues = {
      name: department.name,
    };
  }

  return {
    deleteLoading,
    initialValues,
    onDelete,
    onSubmit,
    schema,
    submitLoading,
  };
};

export const DepartmentForm = ({
  closeModal,
  department,
  isEdit = false,
  setFlashMessage,
}: {
  closeModal: (event?: React.SyntheticEvent) => void;
  department: DealershipDepartment | undefined;
  isEdit?: boolean;
  setFlashMessage: (flashMessage: typeof INITIAL_FLASH_MESSAGE) => void;
}) => {
  const { formatMessage } = useIntl();
  const { deleteLoading, initialValues, onDelete, onSubmit, schema, submitLoading } = useDepartmentForm(department, {
    isEdit,
    setFlashMessage,
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values) => {
        await onSubmit(values);
        closeModal();
      }}
    >
      {(formik) => {
        return (
          <>
            <ModalHeader>
              <Title>
                {isEdit ? (
                  <FormattedMessage id="views.FleetSettings.Departments.actions.editDepartment" />
                ) : (
                  <FormattedMessage id="views.FleetSettings.Departments.actions.newDepartment" />
                )}
              </Title>
              {isEdit && (
                <SecondaryButton intent={BUTTON_INTENT.danger} loading={deleteLoading} onClick={onDelete}>
                  <FormattedMessage id="buttons.delete" />
                </SecondaryButton>
              )}
            </ModalHeader>
            <Form>
              <ModalContent>
                <Field
                  autofill={false}
                  name="name"
                  label={formatMessage({ id: 'views.FleetSettings.Departments.inputLabel' })}
                />
              </ModalContent>
              <ModalFooter>
                <StyledCancelButton onClick={closeModal} />
                <PrimaryButton
                  intent={isEdit ? BUTTON_INTENT.success : BUTTON_INTENT.primary}
                  type="submit"
                  loading={submitLoading}
                  disabled={!formik.isValid || !formik.dirty}
                >
                  {isEdit ? (
                    <FormattedMessage id="views.FleetSettings.Departments.actions.updateDepartment" />
                  ) : (
                    <FormattedMessage id="views.FleetSettings.Departments.actions.createDepartment" />
                  )}
                </PrimaryButton>
              </ModalFooter>
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

interface NewDepartmentButtonProps extends React.ComponentPropsWithoutRef<typeof PrimaryButton> {
  department?: undefined;
  edit?: false;
}

interface ExistingDepartmentButtonProps extends React.ComponentPropsWithoutRef<typeof PrimaryButton> {
  department: DealershipDepartment;
  edit: true;
}

type DepartmentButtonProps = NewDepartmentButtonProps | ExistingDepartmentButtonProps;

const DepartmentButton = ({ department, edit = false, ...rest }: DepartmentButtonProps) => {
  const { formatMessage } = useIntl();
  const [flashMessageState, setFlashMessageState] = useState(INITIAL_FLASH_MESSAGE);

  const { openModal, isOpen, Modal, closeModal } = useModal({
    background: colors.modalBackground,
  });

  const onClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    openModal(event);
  };

  return (
    <>
      {edit ? (
        <SVGButton {...rest} onClick={onClick} color={colors.fleetBlue}>
          <EditIcon color={colors.fleetBlue} />
        </SVGButton>
      ) : (
        <StyledAddDepartmentButton {...rest} onClick={onClick}>
          <FormattedMessage id="views.FleetSettings.Departments.actions.newDepartment" />
        </StyledAddDepartmentButton>
      )}
      {flashMessageState.shown && (
        <FlashMessage
          type={flashMessageState.type}
          text={formatMessage({ id: flashMessageState.translationId }, flashMessageState.translationValueObject)}
          onClose={() => {
            setFlashMessageState(INITIAL_FLASH_MESSAGE);
          }}
          visible
        />
      )}
      {isOpen && (
        <Modal>
          <ModalDialog>
            <DepartmentForm
              department={edit ? department : undefined}
              isEdit={edit}
              setFlashMessage={setFlashMessageState}
              closeModal={closeModal}
            />
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};

export default { DepartmentButton };
